import { constVoid, pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { CommanderThunkAction, WithAvailabilityBlockId, WithServiceId } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { DateTimeLocalizationInfo, DateTimeService, lt, showSuccessAlert, TimeUnitUtils } from '@mews/framework';
import {
    AvailabilityBlock,
    AvailabilityBlockAddParameters,
    AvailabilityBlockUpdateParameters,
} from '@mews-commander/data-access';
import { createUpdateValue } from '@mews-commander/utils';
import { getCurrentEnterpriseId, getStrictCurrentEnterpriseId } from '@mews-commander/core';
import {
    addAvailabilityBlocks,
    getAvailabilityBlockUrl,
    updateAvailabilityBlocks,
    deleteAvailabilityBlocks,
} from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    getAvailabilityBlock,
    deleteAvailabilityBlocksFromState,
} from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import { deleteAvailabilityAdjustmentsByBlockIds } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { parseTimeUnitFieldValue } from '@mews-commander/legacy-form';
import { navigateInBaseModal } from '../app/actions/common';
import { FormValues } from './types';
import { getReleaseStrategy, isEqualUtc } from './utils';

interface MapDatesParams
    extends Pick<
        FormValues,
        'startValue' | 'endValue' | 'date' | 'time' | 'blockReleaseStrategy' | 'offsetDays' | 'offsetTime'
    > {
    timeUnit: TimeUnit;
    timezone: string;
    localizationInfo: DateTimeLocalizationInfo;
}

const mapDates = ({
    startValue,
    endValue,
    date,
    time,
    timeUnit,
    timezone,
    blockReleaseStrategy,
    offsetDays,
    offsetTime,
    localizationInfo,
}: MapDatesParams) => {
    const datePart = TimeUnitUtils.timeUnitToLuxonTimePart(timeUnit);
    const timeunitPart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
    const firstDateTime = parseTimeUnitFieldValue(startValue, localizationInfo, timezone);
    const firstTimeUnitStartUtc = DateTimeService.toUtc(TimeUnitUtils.getTimeUnitStart(firstDateTime, datePart)).value;

    const endDateTime = parseTimeUnitFieldValue(endValue, localizationInfo, timezone);
    const lastDateTimeUnit = DateTimeService.add(timeunitPart)(endDateTime, -1);
    const lastTimeUnitStartUtc = DateTimeService.toUtc(
        TimeUnitUtils.getTimeUnitStart(lastDateTimeUnit, datePart)
    ).value;

    return {
        firstTimeUnitStartUtc,
        lastTimeUnitStartUtc,
        releaseStrategy: getReleaseStrategy({
            blockReleaseStrategy,
            timezone,
            localizationInfo,
            date,
            time,
            offsetDays,
            offsetTime,
        }),
    };
};

export const submitAvailabilityBlockCreateForm =
    ({
        startValue,
        endValue,
        date,
        time,
        blockReleaseStrategy,
        offsetDays,
        offsetTime,
        serviceId,
        ...other
    }: FormValues & WithServiceId): CommanderThunkAction =>
    async (dispatch, getState, { navigate }) => {
        const state = getState();
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        const localizationInfo = getCurrentDateTimeLocalizationInfo(state);
        // @ts-expect-error
        const timeUnit: TimeUnit = getServiceTimeUnit(state, { id: serviceId });
        const { firstTimeUnitStartUtc, lastTimeUnitStartUtc, releaseStrategy } = mapDates({
            startValue,
            endValue,
            date,
            time,
            timezone,
            timeUnit,
            localizationInfo,
            blockReleaseStrategy,
            offsetDays,
            offsetTime,
        });

        const availabilityBlock: AvailabilityBlockAddParameters = {
            firstTimeUnitStartUtc,
            lastTimeUnitStartUtc,
            releaseStrategy,
            voucherId: null,
            budget: undefined,
            serviceId,
            ...other,
        };
        return dispatch(addAvailabilityBlocks({ availabilityBlocks: [availabilityBlock] })).then(
            ({ value: { availabilityBlocks } }: { value: { availabilityBlocks: AvailabilityBlock[] } }) =>
                pipe(
                    availabilityBlocks,
                    array.head,
                    option.match(constVoid, async ({ id }: AvailabilityBlock) => {
                        showSuccessAlert({ description: lt('SaveSuccessMessage') });
                        await navigate({
                            to: '/$currentEnterpriseId/AvailabilityBlock/Dashboard/$availabilityBlockId',
                            params: {
                                currentEnterpriseId: getCurrentEnterpriseId(),
                                availabilityBlockId: id,
                            },
                            replace: true,
                        });
                    })
                )
        );
    };

export const submitAvailabilityBlockUpdateForm =
    ({
        availabilityBlockId,
        startValue,
        endValue,
        date,
        time,
        state,
        name,
        reservationPurpose,
        bookerId,
        companyId,
        travelAgencyId,
        notes,
        pickupDistribution,
        blockReleaseStrategy,
        offsetDays,
        offsetTime,
        purchaseOrderNumber,
    }: FormValues & WithAvailabilityBlockId): CommanderThunkAction =>
    async (dispatch, getState) => {
        const appState = getState();
        const current = getAvailabilityBlock(appState, { id: availabilityBlockId });
        if (!current) return;

        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(appState);
        const localizationInfo = getCurrentDateTimeLocalizationInfo(appState);
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(appState, { id: current.serviceId });
        const { firstTimeUnitStartUtc, lastTimeUnitStartUtc, releaseStrategy } = mapDates({
            startValue,
            endValue,
            date,
            time,
            timezone,
            timeUnit,
            localizationInfo,
            blockReleaseStrategy,
            offsetDays,
            offsetTime,
        });

        const releaseStrategyUpdateValue = createUpdateValue(current.releaseStrategy, releaseStrategy);

        const update: AvailabilityBlockUpdateParameters = {
            availabilityBlockId,
            state: createUpdateValue(current.state, state),
            name: createUpdateValue(current.name, name),
            firstTimeUnitStartUtc: createUpdateValue(current.startUtc, firstTimeUnitStartUtc, isEqualUtc),
            lastTimeUnitStartUtc: createUpdateValue(current.endUtc, lastTimeUnitStartUtc, isEqualUtc),
            releaseStrategy: releaseStrategyUpdateValue?.value
                ? { value: { releaseStrategy: releaseStrategyUpdateValue.value } }
                : undefined,
            reservationPurpose: createUpdateValue(current.reservationPurpose, reservationPurpose),
            bookerId: createUpdateValue(current.bookerId, bookerId),
            companyId: createUpdateValue(current.companyId, companyId),
            travelAgencyId: createUpdateValue(current.travelAgencyId, travelAgencyId),
            notes: createUpdateValue(current.notes, notes),
            pickupDistribution: createUpdateValue(current.pickupDistribution, pickupDistribution),
            purchaseOrderNumber: createUpdateValue(current.purchaseOrderNumber, purchaseOrderNumber),
        };
        return dispatch(updateAvailabilityBlocks({ availabilityBlocks: [update] })).then(() =>
            showSuccessAlert({ description: lt('SaveSuccessMessage') })
        );
    };

export const deleteAvailabilityBlockHeadingAction =
    ({
        availabilityBlockId,
        availabilityBlockName,
    }: WithAvailabilityBlockId & { availabilityBlockName: string }): CommanderThunkAction =>
    (dispatch, getState, { buildLocation }) => {
        const availabilityBlockIds = [availabilityBlockId];

        dispatch(deleteAvailabilityBlocks({ availabilityBlockIds }))
            .then(() => {
                const availabilityBlockReportUrl = buildLocation({
                    to: '/$currentEnterpriseId/AvailabilityBlockReport/Index',
                    params: { currentEnterpriseId: getStrictCurrentEnterpriseId() },
                }).href;
                showSuccessAlert({
                    description: lt('DeleteSuccessMessage', { entity: availabilityBlockName ?? '' }),
                });
                dispatch(navigateInBaseModal(availabilityBlockReportUrl));
            })
            .then(() => {
                dispatch(deleteAvailabilityBlocksFromState(availabilityBlockIds));
                dispatch(deleteAvailabilityAdjustmentsByBlockIds(availabilityBlockIds));
            })
            .catch(() => {
                const availabilityBlockUrl = getAvailabilityBlockUrl(getState(), { id: availabilityBlockId });
                dispatch(navigateInBaseModal(availabilityBlockUrl));
            });
    };
